import { firstChildWithTag, Tag } from "../../components/XmlUtils";
import { isDevelopmentEnvironment } from "../../estada-config";
import Logger from "../../Logger";
import Device from "../Device";
import { CodeSegment } from "./CodeSegment";
import { MapValueToBuffer } from "./MapValueToBuffer";

const logger = new Logger();

export type AssociationListEntry = { addr: string; cob: number; a: any };

export default class DeviceProgramming {
  device: Device;
  codeSegments = new Map<string, CodeSegment>();

  constructor(device: Device) {
    this.device = device;
  }

  getCodeSegment(Id: any) {
    let res = this.codeSegments.get(Id);
    if (!res) {
      throw new Error("CodeSegment not found: " + Id);
    }
    return res;
  }

  static createMappingEntry(data: {
    codeSegement: CodeSegment;
    fullOffset: number;
    fullBitOffset: number;
    type: string;
    sizeInBit: number;
    unionSize: number;
    parameterType: any;
    propertyId: string;
    instanceId: string;
    defaultValue: string;
    name: string;
  }) {
    data.codeSegement.defaultMappings.push(
      new MapValueToBuffer({ ...data, union: undefined, baseOffset: 0, access: "" }),
    );
  }

  initCodeSegments(Static: any) {
    this.codeSegments = new Map();

    const Code = firstChildWithTag(Static, "Code"); // no itx
    if (!Code) {
      return;
    }

    for (let i in Code.items) {
      const item = Code.items[i];
      const Id = item.a.Id;
      const size = parseInt(item.a.Size);
      const Address = parseInt(item.a.Address);
      const Data = firstChildWithTag(item, "Data")?._; // no itx
      const Mask = firstChildWithTag(item, "Mask")?._; // no itx
      const MemoryType = item.a.MemoryType;
      const LoadStateMachine = parseInt(item.a.LoadStateMachine);
      const Offset = parseInt(item.a.Offset);

      const segment = new CodeSegment({
        tag: Tag(item),
        Id: Id,
        Data: Data ? this.device.convertDataURIToBinary(Data) : undefined,
        Mask: Mask ? this.device.convertDataURIToBinary(Mask) : undefined,
        Address: Address,
        MemoryType: MemoryType,
        size: size,
        LoadStateMachine: LoadStateMachine,
        Offset: Offset,
      });
      this.codeSegments.set(Id, segment);
    }
  }

  getCodeSegmentsFromDevice(Static: any) {
    this.codeSegments = new Map();

    const Code = firstChildWithTag(Static, "Code"); // no itx
    if (!Code) {
      return;
    }

    for (let i in Code.items) {
      const item = Code.items[i];
      const Id = item.a.Id;
      const size = parseInt(item.a.Size);
      const Address = parseInt(item.a.Address);
      const Data = firstChildWithTag(item, "Data")?._; // no itx
      const Mask = firstChildWithTag(item, "Mask")?._; // no itx
      const MemoryType = item.a.MemoryType;
      const LoadStateMachine = parseInt(item.a.LoadStateMachine);
      const Offset = parseInt(item.a.Offset);

      const segment = new CodeSegment({
        tag: Tag(item),
        Id: Id,
        Data: Data ? this.device.convertDataURIToBinary(Data) : undefined,
        Mask: Mask ? this.device.convertDataURIToBinary(Mask) : undefined,
        Address: Address,
        MemoryType: MemoryType,
        size: size,
        LoadStateMachine: LoadStateMachine,
        Offset: Offset,
      });
      this.codeSegments.set(Id, segment);
    }

    // if (isDevelopmentEnvironment()) {
    //     let value = device.knxInstanceData.getValue(mapping.propertyId, mapping.instanceId);
    //     const inverseValue = mapping.getValueFromBuffer(codeSegment.Data!);
    // }
  }

  putValuesToCodeSegements(device: Device) {
    const AP = device.getApplicationProgram();
    const LoadProcedureStyle = AP ? AP.a?.LoadProcedureStyle : undefined;

    this.codeSegments.forEach((codeSegment) => {
      if (codeSegment.Data) {
        //
        // DefaultValues to buffer if LoadProcedureStyle is either Merged or DefaultProcedure
        //
        // if (LoadProcedureStyle != "ProductProcedure") {
        codeSegment.defaultMappings.forEach((mapping) => {
          mapping.putValueToBuffer(codeSegment.Data!, device, true, LoadProcedureStyle == "ProductProcedure");
        });
        // }

        //
        // Actual Values to buffer
        //
        codeSegment.mappings.forEach((mapping) => {
          mapping.putValueToBuffer(codeSegment.Data!, device, false);

          if (isDevelopmentEnvironment()) {
            let value = device.knxInstanceData.getValue(mapping.propertyId, mapping.instanceId);
            const inverseValue = mapping.getValueFromBuffer(codeSegment.Data!);
            if (inverseValue != value) {
              console.log(
                "getValueFromBuffer failed for mapping: ",
                mapping,
                "value:",
                value,
                "inverseValue:",
                inverseValue,
              );
            }
          }
        });
      } else {
        logger.error("codeSegment.Data  missing: " + codeSegment.Id);
      }
    });
  }

  putValuesToDevice(device: Device) {
    const allPropertyIds = new Array<{ id: string; instanceId: string }>();
    const allModuleIds = device.knxInstanceData.allModuleIds().concat("");
    allModuleIds.forEach((instanceId2) => {
      device.knxInstanceData.allPropertyIds(instanceId2).forEach((propertyId2) => {
        const v = device.knxInstanceData.getValue(propertyId2, instanceId2);
        if (v != undefined) {
          allPropertyIds.push({ id: propertyId2, instanceId: instanceId2 });
        }
      });
    });

    // allPropertyIds.forEach((property) => {
    //   const ed = device.knxInstanceData.getExtendedData(property.id, property.instanceId);
    //   const value = device.knxInstanceData.getValue(property.id, property.instanceId);
    //   const valueLevel = device.knxInstanceData.getLevel(property.id, property.instanceId);
    //   // const inverseValue = mapping.getValueFromBuffer(codeSegment.Data!);
    // });

    this.codeSegments.forEach((codeSegment) => {
      if (codeSegment.Data) {
        //
        // Actual Values to Device
        //
        codeSegment.mappings.forEach((mapping) => {
          const { propertyId, instanceId } = mapping;

          const currentValue = device.knxInstanceData.getValue(propertyId, instanceId);
          const readbackValue = mapping.getValueFromBuffer(codeSegment.Data!);

          if (readbackValue !== currentValue) {
            // update value in params
            if (!instanceId) {
              const param = device.knxInstanceData.params[propertyId];
              if (param) {
                param.value = readbackValue;
              }
            } else {
              // update value in module params
              const module = device.knxInstanceData.modules[instanceId];
              if (module && module.params[propertyId]) {
                module.params[propertyId].value = readbackValue;
              }
            }

            console.log("ValueDifference", mapping, "value:", currentValue, "inverseValue:", readbackValue);
          }
          // // if (isDevelopmentEnvironment()) {
          // const propertyId = mapping.propertyId;
          // // const foundPropertyInList = allPropertyIds.findLast((item) => {
          // //   item.id == propertyId;
          // // });
          // // const instanceId = foundPropertyInList ? foundPropertyInList.instanceId : mapping.instanceId;
          // const instanceId = mapping.instanceId;

          // const value = device.knxInstanceData.getValue(propertyId, instanceId);
          // // const valueLevel = device.knxInstanceData.getLevel(propertyId, instanceId);
          // const readbackValue = mapping.getValueFromBuffer(codeSegment.Data!);
          // if (readbackValue != value) {
          //   if(instanceId == ""){
          //     if(device.knxInstanceData.params[propertyId])
          //       device.knxInstanceData.params[propertyId].value = readbackValue;
          //   } else{
          //     if(device.knxInstanceData.modules[instanceId] && device.knxInstanceData.modules[instanceId].params[propertyId])
          //       device.knxInstanceData.modules[instanceId].params[propertyId].value = readbackValue;
          //   }

          //   // if (valueLevel != undefined){
          //   //   device.knxInstanceData.setValue(valueLevel, value, propertyId, instanceId, ""); // Doesnt seem to work
          //   // }
          //   console.log("ValueDifference", mapping, "value:", value, "inverseValue:", readbackValue);
          // }
          // // }
        });
      } else {
        logger.error("codeSegment.Data  missing: " + codeSegment.Id);
      }
    });
  }

  getKnxMemorySegments(): Array<CodeSegment> {
    // const res = new Array<CodeSegment>();
    this.putValuesToCodeSegements(this.device);
    const res = [...this.codeSegments.values()];
    return res;
  }

  readBackMemorySegments(): Array<CodeSegment> {
    this.putValuesToDevice(this.device);
    const res = [...this.codeSegments.values()];
    return res;
  }

  // Old logic for Inactive data, kept for reference
  createDefaultMappings1() {
    const ap = this.device.getApplicationProgram();
    if (ap) {
      const Static = firstChildWithTag(ap, "Static"); // no itx
      const Dynamic = firstChildWithTag(ap, "Dynamic"); // no itx
      const Code = firstChildWithTag(Static, "Code"); // no itx
      const Parameters = firstChildWithTag(Static, "Parameters"); // no itx
      const Allocators = firstChildWithTag(Static, "Allocators"); // no itx

      // 27.05.2025: Load all default values in Parameter enclosing Memory tag of Application xml,
      // Later add actual values as shown in Estada's Parameters Tab
      for (let i in Code.items) {
        const item = Code.items[i];
        const segmentId = item.a.Id;
        const segment = this.getCodeSegment(segmentId);

        //
        // insert TopLevel INACTIVE data
        //
        const Parameters = firstChildWithTag(Static, "Parameters"); // no itx
        for (let i in Parameters.items) {
          let parameter = Parameters.items[i];
          const name = parameter["#name"]; //Cound be either "Parameter" or "Union"
          const Access = parameter.a?.Access;
          // if (Access == "None") {
          //     console.log(`Ignore:`, parameter);
          // }
          // else {

          const Memory = firstChildWithTag(parameter, "Memory"); // no itx
          if (Memory && name != "Union") {
            if (Memory.a.CodeSegment == segmentId) {
              const BaseOffsetArgName = Memory.a.BaseOffset;
              if (BaseOffsetArgName) {
                console.log("BaseOffsetArgName:", BaseOffsetArgName);
              }

              const BaseOffset = 0; // getArgValueByRefId(args, BaseOffsetArgName);
              const CodeSegment = Memory.a.CodeSegment;
              let Offset = Memory.a.Offset;
              let BitOffset = Memory.a.BitOffset;

              // if(name == "Union"){
              //   sizeInBit = parseInt(parameter.a.SizeInBit).toString();
              //   parameter = firstChildWithTag(parameter, "Parameter"); //Pick the first child and set it as default for the corresponding memory
              //   Offset = (parseInt(Offset) + parseInt(parameter.a.Offset)).toString()
              //   BitOffset = (parseInt(BitOffset) + parseInt(parameter.a.BitOffset)).toString()
              // }

              const ParameterTypeId = parameter?.a.ParameterType;
              const parameterType = this.device.template?.knxProductData?.globalMap.get(ParameterTypeId);
              const parameterRestriction = parameterType?.TR || parameterType?.TypeRestriction;

              let type = "none";
              let sizeInBit = "0";
              if (parameterRestriction) {
                type = "unsignedInt";
                sizeInBit = parameterRestriction?.a.SizeInBit;
              } else {
                if (parameterType) {
                  if (parameterType.TypeText) {
                    type = "text";
                    sizeInBit = parameterType.TypeText.a.SizeInBit;
                  } else if (parameterType.TypeNumber) {
                    type = parameterType.TypeNumber.a.Type || "number";
                    sizeInBit = parameterType.TypeNumber.a.SizeInBit;
                  }
                }
              }

              // Check type and sizeInBit

              DeviceProgramming.createMappingEntry({
                codeSegement: segment,
                fullOffset: parseInt(Offset),
                fullBitOffset: parseInt(BitOffset),
                type: type,
                sizeInBit: parseInt(sizeInBit),
                unionSize: 0,
                parameterType: parameterType,
                propertyId: parameter.a.Id,
                instanceId: "", // this handles topLevel Properties ONLY
                defaultValue: parameter.a.Value,
                name: parameter.a?.Name,
              });
            }
          }
          // }
        }

        //
        // insert INACTIVE data from modules
        //
        const Repeat = firstChildWithTag(Dynamic, "Repeat"); // no itx
        let Count = 0;
        if (Repeat) {
          if (Repeat.a.Count !== undefined) {
            Count = parseInt(Repeat.a.Count);
          } else if (Repeat.a.ParameterRefId) {
            Count = parseInt(this.device.knxInstanceData.getValue(Repeat.a.ParameterRefId, Repeat.a.Id));
          }
        }
        const Module = Repeat?.Module;
        const args: any = [];
        if (Module) {
          for (let i in Module.items) {
            const item = Module.items[i];
            const tag = Tag(item);
            if (tag == "NumericArg" || tag == "TextArg") {
              args.push(item);
            }
          }
        }

        const ModuleDefs = firstChildWithTag(ap, "ModuleDefs"); // no itx
        if (ModuleDefs) {
          for (let i in ModuleDefs.items) {
            let moduleDef = ModuleDefs.items[i];
            const moduleId = moduleDef.a?.Id || "";

            const Arguments = firstChildWithTag(moduleDef, "Arguments"); // no itx
            const Static = firstChildWithTag(moduleDef, "Static"); // no itx

            const Parameters = firstChildWithTag(Static, "Parameters"); // no itx
            for (let i in Parameters.items) {
              let parameter = Parameters.items[i];
              const name = parameter["#name"]; //Cound be either "Parameter" or "Union"
              const Access = parameter.a?.Access;
              // if (Access == "None") {
              //     console.log(`Ignore:`, parameter);
              // } else {
              const Memory = firstChildWithTag(parameter, "Memory"); // no itx
              if (Memory && name != "Union") {
                if (Memory.a.CodeSegment == segmentId) {
                  const BaseOffsetArgName = Memory.a.BaseOffset;
                  let allocates = "0";
                  if (BaseOffsetArgName) {
                    console.log("BaseOffsetArgName:", BaseOffsetArgName);
                    const argument = Arguments.items.filter((item) => item.a.Id == BaseOffsetArgName);
                    if (argument) {
                      allocates = argument[0].a.Allocates;
                    }
                  }

                  let BaseOffset = "0";
                  let Allocator: any;
                  if (args) {
                    for (let i in args) {
                      const arg = args[i];
                      if (arg.a.RefId == BaseOffsetArgName) {
                        const AllocatorRefId = arg.a.AllocatorRefId;
                        if (AllocatorRefId) {
                          Allocator = Allocators.items.find((allocator: any) => allocator.a.Id == AllocatorRefId);
                          BaseOffset = Allocator.a.Start;
                          break;
                        }
                        BaseOffset = arg.a.Value;
                        break;
                      }
                    }
                  }
                  const CodeSegment = Memory.a.CodeSegment;
                  let OffsetMemory = Memory.a.Offset;
                  let BitOffsetMemory = Memory.a.BitOffset;

                  const ParameterBitOffset = parameter.a.BitOffset;
                  const ParameterOffset = parameter.a.Offset;

                  const ParameterTypeId = parameter?.a.ParameterType;
                  const parameterType = this.device.template?.knxProductData?.globalMap.get(ParameterTypeId);
                  const parameterRestriction = parameterType?.TR || parameterType?.TypeRestriction;

                  // Check type and sizeInBit
                  let type = "none";
                  let sizeInBit = "0";
                  if (parameterRestriction) {
                    type = "unsignedInt";
                    sizeInBit = parameterRestriction?.a.SizeInBit;
                  } else {
                    if (parameterType) {
                      if (parameterType.TypeText) {
                        type = "text";
                        sizeInBit = parameterType.TypeText.a.SizeInBit;
                      } else if (parameterType.TypeNumber) {
                        type = parameterType.TypeNumber.a.Type || "number";
                        sizeInBit = parameterType.TypeNumber.a.SizeInBit;
                      }
                    }
                  }

                  let value = parameter.a.Value;

                  for (let repeatIndex = 0; repeatIndex < Count; repeatIndex++) {
                    let fullOffset = 0;
                    if (Allocator) {
                      const baseOffsetFromAllocator = parseInt(Allocator.a.Start);
                      const maxInclusive = parseInt(Allocator.a.maxInclusive);

                      fullOffset = baseOffsetFromAllocator + repeatIndex * parseInt(allocates);
                      fullOffset += parseInt(OffsetMemory || "0");
                      fullOffset += parseInt(ParameterOffset || "0");
                    } else {
                      fullOffset = parseInt(BaseOffset || "0");
                      fullOffset += parseInt(OffsetMemory || "0");
                      fullOffset += parseInt(ParameterOffset || "0");
                    }

                    DeviceProgramming.createMappingEntry({
                      codeSegement: segment,
                      fullOffset: fullOffset,
                      fullBitOffset: parseInt(BitOffsetMemory),
                      type: type,
                      sizeInBit: parseInt(sizeInBit),
                      unionSize: 0,
                      parameterType: parameterType,
                      defaultValue: value,
                      propertyId: parameter.a.Id,
                      instanceId: moduleId,
                      name: parameter.a?.Name,
                    });
                  }
                }
              }
              // }
            }
          }
        }
      }
    }

    // <Static>
    // <Code>
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-4000" Size="513" Address="16384">
    //     <Data>AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA</Data>
    //     <Mask>/wAA////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////</Mask>
    //   </AbsoluteSegment>
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-4201" Size="511" Address="16897">
    //     <Data>AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==</Data>
    //   </AbsoluteSegment>
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-0700" Size="666" Address="1792" />
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-4400" Size="656" Address="17408">
    //     <Data>owcAB6TfAwfY3wMIDN8ACEDfAAh03wAIqN8ACNzfAAeo3wMH3N8DCBDfAAhE3wAIeN8ACKzfAAjd3wAHrN8DB+DfAwgU3wAISN8ACHzfAAiw3wAI3t8AB7DfAwfk3wMIGN8ACEzfAAiA3wAItN8ACN/fAAe03wMH6N8DCBzfAAhQ3wAIhN8ACLjfAAjg3wAHuN8DB+zfAwgg3wAIVN8ACIjfAAi83wAI4d8AB7zfAwfw3wMIJN8ACFjfAAiM3wAIwN8ACOLfAAfA3wMH9N8DCCjfAAhc3wAIkN8ACMTfAAjj3wAHxN8DB/jfAwgs3wAIYN8ACJTfAAjI3wAI5N8AB8jfAwf83wMIMN8ACGTfAAiY3wAIzN8ACOXfAAfM3wMIAN8DCDTfAAho3wAInN8ACNDfAAjm3wAH0N8DCATfAwg43wAIbN8ACKDfAAjU3wAI598AB9TfAwgI3wMIPN8ACHDfAAik3wAI2N8ACOjfAAjp3wAI7d8ACPHfAAjq3wAI7t8ACPLfAAjr3wAI798ACPPfAAjs3wAI8N8ACPTfAAj13wAI9t8ACPffAAj43wAI+d8ACPrfAAj73wAI/N8ACP3fAAj+3wAI/98ACQDfAAkB3wAJAt8ACQPfAAkE3wAJBd8ACQbfAAkH3wAJCN8ACQnfAAkK3wAJC98ACQzfAAkN3wAJDt8ACQ/fAAkQ3wAJEd8ACRLfAAkT3wAJFN8ACRbfCAkY3wgJGt8ACRvfAAkc3wkJH98JCSLfDAkq3wAJK98ACSzfAAkt3wAJLt8OCTzfDglK3w4JWN8ICWbfCAl03wgJgt8ICZDfAAmR3wAJkt8ACZPfAAmU3wAJld8ACZbfAAmX3wAJmN8ACZrfAAA=</Data>
    //   </AbsoluteSegment>
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-099A" Size="1" MemoryType="RAM" Address="2458" />
    //   <AbsoluteSegment Id="M-0083_A-018A-30-BDF6_AS-4690" Size="1968" MemoryType="EEPROM" Address="18064">
    //     <Data>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</Data>
    //   </AbsoluteSegment>
    // </Code>
  }

  // Old logic for Active data, kept for reference
  createDefaultMappingsOld() {
    const ap = this.device.getApplicationProgram();
    if (ap) {
      const Static = firstChildWithTag(ap, "Static"); // no itx
      const Dynamic = firstChildWithTag(ap, "Dynamic"); // no itx
      const Code = firstChildWithTag(Static, "Code"); // no itx
      const Parameters = firstChildWithTag(Static, "Parameters"); // no itx
      const Allocators = firstChildWithTag(Static, "Allocators"); // no itx

      // 27.05.2025: Load all default values in Parameter enclosing Memory tag of Application xml,
      // Later add actual values as shown in Estada's Parameters Tab
      for (let i in Code.items) {
        const item = Code.items[i];
        const segmentId = item.a.Id;
        const segment = this.getCodeSegment(segmentId);

        //
        // insert "aktive" data from Estada
        //

        //
        // Load all data from main (without modules)
        //

        //
        // segment.Data is a fresh copy of the binary data from ApplicationXml
        //

        const instanceData = this.device.knxInstanceData;
        for (let i in instanceData.extData) {
          const mem = instanceData.extData[i]?.mem;
          const access = instanceData.extData[i]?.access;
          // if (access == "None") {
          //     console.log(`Ignore:`, mem);
          // } else {
          if (mem) {
            console.log(i, mem);
            if (mem.CodeSegment == segmentId) {
              let value = instanceData.getValue(i, undefined, false);
              // const value = instanceData.getValue(i, undefined, true);

              // const union = mem.union;
              // let unionSize = 0;
              // if(union){
              const sUnionSize = mem.union?.a.SizeInBit || "8";
              const unionSize = parseInt(sUnionSize);
              //   const unionMem = union.Memory;
              //   if(unionMem){
              //     console.log("found unionMem: ",unionMem);
              //   }
              // }

              //   const param = mem.parmeterType.items[0];
              //   const paramName = param["#name"];

              //   if(value == "" && paramName == "TypeText"){
              //     value = "-/-"
              //   }

              // DeviceProgramming.createMappingEntry(
              //   segment,
              //   mem.fullOffset,
              //   mem.fullBitOffset,
              //   mem.type,
              //   mem.sizeInBit,
              //   unionSize,
              //   mem.parmeterType,
              // );

              DeviceProgramming.createMappingEntry({
                codeSegement: segment,
                fullOffset: mem.fullOffset,
                fullBitOffset: mem.fullBitOffset,
                type: mem.type,
                sizeInBit: mem.sizeInBit,
                unionSize: unionSize,
                parameterType: mem.parameterType,
                defaultValue: value,
                propertyId: "???",
                instanceId: "???",
                name: "???",
              });
            }
          }
          // }
        }

        //
        // Load all data from modules
        //

        for (let im in instanceData.modules) {
          const module = instanceData.modules[im];
          // TargeParamRefRef under Assign tag is also part of Active Parameters (modified in ETS during device download)
          instanceData.assigns.forEach((assign, id) => {
            const moduleParamRefId = id.split("_R-")[0];
            const paramRefId = id.split("_")[1];

            const knxProductData = this.device.template?.knxProductData;
            const mParam = knxProductData?.getItem(moduleParamRefId);
            const parameterType = knxProductData?.getItem(mParam?.a.ParameterType);
            let mem;
            if (mParam?.Memory) {
              mem = mParam.Memory;
            } else if (mParam?.union) {
              mem = mParam.union?.Memory;
            } else {
              const param = knxProductData?.getItem(paramRefId);
              mem = param?.items.length > 0 ? param?.items[0] : undefined;
            }
            const refMem = module?.extData ? module?.extData[assign.toId]?.mem : undefined;
            if (mem && refMem) {
              console.log(im, id, mem);
              if (mem.a.CodeSegment == segmentId) {
                const value = instanceData.getValue(id, assign.instanceId, true);
                const sUnionSize = mParam?.union?.a.SizeInBit || "8";
                const unionSize = parseInt(sUnionSize);
                // DeviceProgramming.createMappingEntry(
                //   segment,
                //   refMem.baseOffset + parseInt(mem.a.Offset),
                //   parseInt(mem.a.BitOffset),
                //   "none",
                //   mem.a?.sizeInBit || 0,
                //   unionSize,
                //   parameterType,
                // );
                DeviceProgramming.createMappingEntry({
                  codeSegement: segment,
                  fullOffset: refMem.baseOffset + parseInt(mem.a.Offset),
                  fullBitOffset: parseInt(mem.a.BitOffset),
                  type: "none",
                  sizeInBit: mem.a?.sizeInBit || 0,
                  unionSize: unionSize,
                  parameterType: parameterType,
                  defaultValue: value,
                  propertyId: "???",
                  instanceId: "???",
                  name: "???",
                });
              }
            }
          });

          for (let i in module.extData) {
            const mem = module.extData[i]?.mem;
            if (mem) {
              console.log(im, i, mem);
              if (mem.CodeSegment == segmentId) {
                const value = instanceData.getValue(i, im);
                const sUnionSize = mem.union?.a.SizeInBit || "8";
                const unionSize = parseInt(sUnionSize);
                // DeviceProgramming.createMappingEntry(
                //   segment,
                //   mem.fullOffset,
                //   mem.fullBitOffset,
                //   mem.type,
                //   mem.sizeInBit || 0,
                //   unionSize,
                //   mem.parmeterType,
                // );
                DeviceProgramming.createMappingEntry({
                  codeSegement: segment,
                  fullOffset: mem.fullOffset,
                  fullBitOffset: mem.fullBitOffset,
                  type: mem.type,
                  sizeInBit: mem.sizeInBit || 0,
                  unionSize: unionSize,
                  parameterType: mem.parmeterType,
                  defaultValue: value,
                  propertyId: "???",
                  instanceId: "???",
                  name: "???",
                });
              }
            }
          }
        }

        // res.push(segment);
      }
    }
  }

  // In Use
  createDefaultMappings() {
    const ap = this.device.getApplicationProgram();
    if (!ap) return;

    const Static = firstChildWithTag(ap, "Static"); // no itx
    const Dynamic = firstChildWithTag(ap, "Dynamic"); // no itx
    const Code = firstChildWithTag(Static, "Code"); // no itx
    const Parameters = firstChildWithTag(Static, "Parameters"); // no itx
    const Allocators = firstChildWithTag(Static, "Allocators"); // no itx
    const ModuleDefs = firstChildWithTag(ap, "ModuleDefs"); // no itx

    const extData = this.device.knxInstanceData.extData;

    // Helper: Recursively walk parameter tree
    const walkParameters = (params: any[], segmentId: string, segment: CodeSegment, instanceId: string = "") => {
      for (const param of params) {
        const name = param["#name"];
        if (name === "Parameter") {
          const Memory = firstChildWithTag(param, "Memory"); // no itx
          if (Memory && Memory.a.CodeSegment === segmentId) {
            const ParameterTypeId = param?.a.ParameterType;
            const parameterType = this.device.template?.knxProductData?.globalMap.get(ParameterTypeId);
            const parameterRestriction = parameterType?.TR || parameterType?.TypeRestriction;
            let type = "none";
            let sizeInBit = "0";
            if (parameterRestriction) {
              type = "unsignedInt";
              sizeInBit = parameterRestriction?.a.SizeInBit;
            } else if (parameterType) {
              if (parameterType.TypeText) {
                type = "text";
                sizeInBit = parameterType.TypeText.a.SizeInBit;
              } else if (parameterType.TypeNumber) {
                type = parameterType.TypeNumber.a.Type || "number";
                sizeInBit = parameterType.TypeNumber.a.SizeInBit;
              }
            }
            DeviceProgramming.createMappingEntry({
              codeSegement: segment,
              fullOffset: parseInt(Memory.a.Offset || "0"),
              fullBitOffset: parseInt(Memory.a.BitOffset || "0"),
              type,
              sizeInBit: parseInt(sizeInBit),
              unionSize: 0,
              parameterType,
              propertyId: param.a.Id,
              instanceId,
              defaultValue: param.a.Value,
              name: param.a.Name,
            });
          }
        }
        // Handle Union
        if (name === "Union" && param.items) {
          walkParameters(param.items, segmentId, segment, instanceId);
        }
        // Handle Choose/When (conditional)
        if (name === "Choose" && param.items) {
          for (const when of param.items) {
            if (when.items) walkParameters(when.items, segmentId, segment, instanceId);
          }
        }
        // Handle nested Parameters
        if (param.items) {
          walkParameters(param.items, segmentId, segment, instanceId);
        }
      }
    };

    if (Code) {
      for (let i in Code.items) {
        const item = Code.items[i];
        const segmentId = item.a.Id;
        const segment = this.getCodeSegment(segmentId);

        // Walk all parameters for this segment (main)
        if (Parameters && Parameters.items) {
          walkParameters(Parameters.items, segmentId, segment, "");
        }

        // Walk module parameters for this segment
        if (ModuleDefs && ModuleDefs.items) {
          for (const moduleDef of ModuleDefs.items) {
            const moduleStatic = firstChildWithTag(moduleDef, "Static"); // no itx
            const moduleParams = moduleStatic && firstChildWithTag(moduleStatic, "Parameters"); // no itx
            // You may want to get the moduleId from moduleDef.a.Id or similar
            const moduleId = moduleDef.a?.Id || "";
            if (moduleParams && moduleParams.items) {
              walkParameters(moduleParams.items, segmentId, segment, moduleId);
            }
          }
        }
      }
    }
  }

  getLoadProcedures(): any {
    let res;
    const ap = this.device.getApplicationProgram();
    if (ap) {
      const Static = firstChildWithTag(ap, "Static"); // no itx
      const LoadProcedures = firstChildWithTag(Static, "LoadProcedures"); // no itx
      res = LoadProcedures;
    }
    return res;
  }

  // async getFullProcedure() {
  //   let res;
  //   const xmlData = await this.device.template?.knxProductData.loadXmlData();
  //   const KNX = xmlData?.KNX;
  //   const ManufacturerData = AllChildsWithTag(KNX, "ManufacturerData")[0];
  //   const Manufacturer = AllChildsWithTag(ManufacturerData, "Manufacturer")[0];
  //   const ApplicationPrograms = AllChildsWithTag(Manufacturer, "ApplicationPrograms")[0];

  //   if (ApplicationPrograms && ApplicationPrograms?.items?.length == 1) {
  //     const ApplicationProgram = ApplicationPrograms.items[0];
  //     const Static = AllChildsWithTag(ApplicationProgram, "Static")[0]; // no itx
  //     const LoadProcedures = AllChildsWithTag(Static, "LoadProcedures")[0]; // no itx
  //     res = LoadProcedures;
  //   }
  //   return res;
  // }

  getSignalAssociationList() {
    const signalAssociationList = new Array<AssociationListEntry>();

    this.device.allKnxSignals().forEach((signal) => {
      // if (!signal.isDummySignal) {
      const a = (signal as any)?.a;
      const knxSignal = signal.knxSignal;
      if (a) {
        if (knxSignal && knxSignal.groupAddresses.items.length > 0) {
          // Include signals with associated GroupAddresses
          knxSignal.groupAddresses.forEach((groupAddress) => {
            signalAssociationList.push({
              addr: groupAddress.fullAddress,
              cob: parseInt(signal.number),
              a: a,
            });
          });
        } else {
          // Also include signals with no associations as needed in forming ComObjTable
          signalAssociationList.push({
            addr: "",
            cob: parseInt(signal.number),
            a: a,
          });
        }
      } else {
        logger.error("missing a in signal");
      }
      // }
    });

    return signalAssociationList;
  }
}
